// The fee reserve's affordability rule (docs/18-one-fee-reserve.md section 5.1 A; R78): one pure function for every
// requirement the app's engine checks before any key is reserved, and for every figure a page shows, so the gates,
// the counts and the panels cannot disagree. Nothing here reads the network: the caller reads, immediately before
// the check, F = getMinimumBalanceForRentExemption(0) (the rent-exempt minimum of a 0-byte account, which the fee
// payer must keep after the fee and at the end of the transaction unless it ends at exactly 0; docs/14 section 7,
// E8), the reserve's balance B, and the live rent of each account the transaction creates.
//
// Also here, for the activation marker (5.1 D step 7; R79): the per-attempt compute-unit limit that distinguishes
// two Initialize carriers built on one blockhash, probabilistically (equal draws still give identical carriers).
//
// And the sweep's plan (docs/18 section 6): which form moves an older reserve into the current one, or why none can.

import { type LeafRecord, UNRESOLVED } from './ledger.js';
import { type Limits, PLAIN_LIMITS } from './v1.js';

/** The base fee of one signature, in lamports. */
export const BASE_FEE_PER_SIGNATURE = 5_000n;
/** K: one plain action's fee (5,000 base + 10,000 priority) held back so a key refresh after a failed attempt can still be paid for (R49; docs/18 5.1 A). */
export const KEY_REFRESH_HOLD = 15_000n;
/** The fee of one plain, single-signature action with the app's priority fee: a key refresh, a native withdrawal, a Send out, Activate. */
export const PLAIN_ACTION_FEE = 15_000n;
/** The ten-action allowance a suggested top-up adds on top of F + K (docs/18 5.1 A, "Suggested top-up on Deposit"; policy, not a requirement). */
export const SUGGESTED_ACTIONS = 10n;

/** The network fee of a transaction with `limits`: 5,000 lamports per signature plus the absolute priority fee v1 carries (sdk/ts/src/v1.ts). */
export function feeOf(limits: Limits, signatures = 1): bigint {
  return BASE_FEE_PER_SIGNATURE * BigInt(signatures) + limits.priorityFeeLamports;
}

/**
 * A rent-exempt minimum at a fixed rent schedule: (data length + 128) × lamports per byte. For tests and examples only
 * (mainnet's 5,080 and the shim's 6,960 lamports per byte); the app always reads the live figure.
 */
export function rentAtSchedule(dataLen: number, lamportsPerByte: bigint): bigint {
  return BigInt(dataLen + 128) * lamportsPerByte;
}

/** One requirement, itemised: B must be at least `total` = `minimum` + `fee` + `rents` + `hold`. */
export interface Requirement {
  /** F, the fee payer's account minimum, returned when the reserve is emptied. */
  minimum: bigint;
  fee: bigint;
  /** Rent for the accounts the transaction creates (for activation: the vault's actual shortfall). */
  rents: bigint;
  /** K, held for a key refresh (0 for the key refresh itself). */
  hold: bigint;
  total: bigint;
}

function requirement(minimum: bigint, fee: bigint, rents: bigint, hold: bigint): Requirement {
  if (minimum < 0n || fee < 0n || rents < 0n || hold < 0n) throw new RangeError('requirement terms are never negative');
  return { minimum, fee, rents, hold, total: minimum + fee + rents + hold };
}

/** An ordinary action (withdrawals, wrap, unwrap, swap): F + f + rents + K. */
export function ordinaryRequirement(i: { minimum: bigint; fee: bigint; rents: readonly bigint[]; hold?: bigint }): Requirement {
  return requirement(i.minimum, i.fee, i.rents.reduce((s, r) => s + r, 0n), i.hold ?? KEY_REFRESH_HOLD);
}

/** One plain action, Home's low-reserve threshold: F + 15,000 + K. */
export function plainActionRequirement(minimum: bigint): Requirement {
  return requirement(minimum, PLAIN_ACTION_FEE, 0n, KEY_REFRESH_HOLD);
}

/** Activation: F + f + K + max(0, rent(128) − what the vault's address already holds), a prefunded vault's real shortfall. */
export function activationRequirement(i: { minimum: bigint; fee: bigint; vaultRent: bigint; vaultLamports: bigint; hold?: bigint }): Requirement {
  const shortfall = i.vaultRent > i.vaultLamports ? i.vaultRent - i.vaultLamports : 0n;
  return requirement(i.minimum, i.fee, shortfall, i.hold ?? KEY_REFRESH_HOLD);
}

/** The key refresh, checked before `recover()` reserves leaf 255: F + f, with no hold (it is the action the hold is for). */
export function refreshRequirement(i: { minimum: bigint; fee: bigint }): Requirement {
  return requirement(i.minimum, i.fee, 0n, 0n);
}

/** Whether a balance meets a requirement; equality is accepted. */
export function affordable(balance: bigint, req: Pick<Requirement, 'total'>): boolean {
  return balance >= req.total;
}

/** "About N actions": N = floor((B − F − K) / 15,000) when B ≥ F + K, else 0. */
export function actionCount(balance: bigint, minimum: bigint, hold: bigint = KEY_REFRESH_HOLD, perAction: bigint = PLAIN_ACTION_FEE): number {
  if (balance < minimum + hold) return 0;
  return Number((balance - minimum - hold) / perAction);
}

/** The suggested top-up on Deposit: F + K + 10 × 15,000. A suggestion, labelled as one; never a gate. */
export function suggestedTopUp(minimum: bigint): bigint {
  return minimum + KEY_REFRESH_HOLD + SUGGESTED_ACTIONS * PLAIN_ACTION_FEE;
}

export type SendOutRefusal =
  /** The amount is zero or negative. */
  | 'nothing'
  /** B − fee is neither 0 nor at least F: nothing can be sent out (B < F + fee). */
  | 'below-minimum'
  /** The amount is more than B − fee. */
  | 'over-balance'
  /** B − fee − x would be neither 0 nor at least F. */
  | 'leaves-below-minimum'
  /** The destination holds less than F and would still hold less than F after the transfer (a new account must receive at least F). */
  | 'destination-below-minimum';

export interface SendOutLimits {
  /** "Everything but the fee": B − fee, or null when nothing can be sent out. */
  everything: bigint | null;
  /** The largest partial amount, B − fee − F, or null when none is allowed. */
  maxPartial: bigint | null;
}

export type SendOutCheck = ({ ok: true; endBalance: bigint } | { ok: false; reason: SendOutRefusal }) & SendOutLimits;

/** What Send out can move from a balance B: everything (the reserve ends at 0) or at most B − fee − F, and nothing below F + fee. */
export function sendOutLimits(balance: bigint, minimum: bigint, fee: bigint = PLAIN_ACTION_FEE): SendOutLimits {
  const afterFee = balance - fee;
  if (afterFee <= 0n || afterFee < minimum) return { everything: null, maxPartial: null };
  return { everything: afterFee, maxPartial: afterFee - minimum > 0n ? afterFee - minimum : null };
}

/**
 * Send out of x > 0 from the reserve (docs/18 5.1 A, the last row; M18). The payer is checked after the fee and again
 * at the end of the transaction: B − fee is 0 or at least F, and B − fee − x is 0 or at least F. So x = B − fee
 * ("Everything but the fee", the reserve ends at 0) or x ≤ B − fee − F, and nothing is accepted below F + fee. A
 * destination read just before signing that holds less than F must end at least at F (a destination holding 0, a new
 * account, must receive at least F; this is the runtime's rule for a System account, which cannot stay below its
 * minimum when credited). `destinationLamports` undefined skips the destination check (not yet read).
 */
export function sendOutCheck(i: { balance: bigint; minimum: bigint; amount: bigint; fee?: bigint; destinationLamports?: bigint }): SendOutCheck {
  const fee = i.fee ?? PLAIN_ACTION_FEE;
  const limits = sendOutLimits(i.balance, i.minimum, fee);
  if (i.amount <= 0n) return { ok: false, reason: 'nothing', ...limits };
  const afterFee = i.balance - fee;
  if (afterFee < 0n || (afterFee !== 0n && afterFee < i.minimum)) return { ok: false, reason: 'below-minimum', ...limits };
  if (i.amount > afterFee) return { ok: false, reason: 'over-balance', ...limits };
  const end = afterFee - i.amount;
  if (end !== 0n && end < i.minimum) return { ok: false, reason: 'leaves-below-minimum', ...limits };
  if (i.destinationLamports !== undefined && i.destinationLamports < i.minimum && i.destinationLamports + i.amount < i.minimum) return { ok: false, reason: 'destination-below-minimum', ...limits };
  return { ok: true, endBalance: end, ...limits };
}

// ------------------------------------------------------------- activation carriers (5.1 D step 7)

/** Initialize's compute-unit limit is ACTIVATION_CU_BASE + r, with r drawn per attempt uniformly from [0, ACTIVATION_CU_SPREAD). */
export const ACTIVATION_CU_BASE = 200_000;
export const ACTIVATION_CU_SPREAD = 65_536;

/**
 * The limits of one Initialize carrier: `plain` with `computeUnitLimit` = 200,000 + r, every other field unchanged
 * (the absolute priority fee is serialised apart from the compute-unit limit, so the fee does not change). r must be an
 * integer in [0, 65,536). Two attempts on one blockhash and one payer collide only if their r values are equal
 * (probability 2^-16 per pair); this distinguishes carriers probabilistically, it does not make them unique.
 */
export function activationLimitsFrom(plain: Limits, r: number): Limits {
  if (!Number.isInteger(r) || r < 0 || r >= ACTIVATION_CU_SPREAD) throw new RangeError(`the activation draw must be an integer in [0, ${ACTIVATION_CU_SPREAD}), got ${r}`);
  return { ...plain, computeUnitLimit: ACTIVATION_CU_BASE + r };
}

/** One draw of r for an Initialize carrier, uniform in [0, 65,536), from `crypto.getRandomValues`. */
export function drawActivationR(random: (a: Uint16Array) => Uint16Array = (a) => crypto.getRandomValues(a)): number {
  return random(new Uint16Array(1))[0]!;
}

// ------------------------------------------------------------- the sweep (docs/18 section 6)

/** Form A's fee: one signature (the older key pays alone) plus `PLAIN_LIMITS`' priority fee. */
const SWEEP_FEE_A = feeOf(PLAIN_LIMITS, 1);
/** Form B's fee: two signatures (the current reserve pays, the older key co-signs) plus the priority fee. */
const SWEEP_FEE_B = feeOf(PLAIN_LIMITS, 2);

/**
 * What a sweep of an older reserve can do now. `A`: the older key pays and moves `amount` = L − fee, ending at 0.
 * `B`: the current reserve pays and all of L moves. `not-yet`: add at least `add` to the current reserve first.
 * `nothing`: the older reserve is empty. `blocked`: the guard holds (an unresolved non-recovery record).
 */
export type SweepPlan = { form: 'A'; amount: bigint; fee: bigint; payer: 'source' } | { form: 'B'; amount: bigint; fee: bigint; payer: 'active' } | { form: 'not-yet'; add: bigint } | { form: 'nothing' } | { form: 'blocked' };

/**
 * docs/18 section 6's table and guard, with L = `older` (the older reserve's balance), C = `current` (the current
 * reserve's) and F = `minimum`, all read immediately before by the caller. L = 0 is nothing to move. `records` (the
 * current tree's ledger records) is the guard's input: any unresolved record other than a recovery record blocks, as
 * its wire may still be in flight, paid by the older key, and would fail and burn its leaf if that key were emptied.
 * A recovery record names no payer and never blocks. `records` is required so that a caller cannot skip the guard by
 * leaving it out: the guard is vacuous only when no signer exists (no ledger records at all), and that caller passes
 * []. Then L ≥ F + 15,000 is Form A; otherwise C ≥ F + 20,000 is Form B; otherwise not yet, with F + 20,000 − C to
 * add. Both forms leave the older account at exactly 0, which the payer rule accepts. The caller refuses the sweep
 * before this when either account is not an empty System account.
 */
export function sweepPlan(i: { older: bigint; current: bigint; minimum: bigint; records: readonly LeafRecord[] }): SweepPlan {
  const { older: L, current: C, minimum: F } = i;
  if (L < 0n || C < 0n || F < 0n) throw new RangeError('balances and the minimum are never negative');
  if (L === 0n) return { form: 'nothing' };
  if (i.records.some((r) => UNRESOLVED.includes(r.status) && r.purpose !== 'recovery')) return { form: 'blocked' };
  if (L >= F + SWEEP_FEE_A) return { form: 'A', amount: L - SWEEP_FEE_A, fee: SWEEP_FEE_A, payer: 'source' };
  if (C >= F + SWEEP_FEE_B) return { form: 'B', amount: L, fee: SWEEP_FEE_B, payer: 'active' };
  return { form: 'not-yet', add: F + SWEEP_FEE_B - C };
}
